import { useEffect, useState } from 'react';
import { useLanguage } from '@/i18n/LanguageProvider';
import type { ExerciseConfig } from '@/pages/FitnessWorkout';
import { FITNESS_WORKOUT_FINISH_LIMITS as LIMITS } from '../../../supabase/functions/_shared/fitnessWorkoutFinishDomain';
import WorkoutNumberInput from './WorkoutNumberInput';

interface WorkoutSetupProps {
  exerciseName: string; timed?: boolean; muscleGroup: string;
  defaultWeight: number; defaultReps: number; defaultSets: number;
  onComplete: (config: ExerciseConfig) => void;
}
export default function WorkoutSetup({ exerciseName, timed = false, muscleGroup, defaultWeight, defaultReps, defaultSets, onComplete }: WorkoutSetupProps) {
  const { t } = useLanguage();
  const [config, setConfig] = useState({ weight: defaultWeight, reps: defaultReps, sets: defaultSets });
  const [invalid, setInvalid] = useState<Set<string>>(() => new Set());
  useEffect(() => { setConfig({ weight: defaultWeight, reps: defaultReps, sets: defaultSets }); setInvalid(new Set()); }, [defaultWeight, defaultReps, defaultSets]);
  const valid = (key: string, value: boolean) => setInvalid(current => {
    if (value === !current.has(key)) return current;
    const next = new Set(current); if (value) next.delete(key); else next.add(key); return next;
  });
  return <div className="mx-auto flex w-full max-w-lg flex-1 flex-col justify-center px-4 py-8">
    <p className="mb-2 text-xs uppercase tracking-[0.18em] text-primary">{muscleGroup}</p>
    <h2 className="mb-8 font-display text-3xl font-semibold leading-tight">{exerciseName}</h2>
    <div className="grid grid-cols-2 gap-4">
      <WorkoutNumberInput label={t('fitness.workout.weightLabel')} value={config.weight} min={0} max={LIMITS.weight}
        onChange={weight => setConfig(current => ({ ...current, weight }))} onValidityChange={value => valid('weight', value)} />
      <WorkoutNumberInput label={t(timed ? 'fitness.ux.seconds' : 'fitness.workout.reps')} value={config.reps} min={1} max={LIMITS.reps} integer
        onChange={reps => setConfig(current => ({ ...current, reps }))} onValidityChange={value => valid('reps', value)} />
      <WorkoutNumberInput label={t('fitness.workout.sets')} value={config.sets} min={1} max={LIMITS.sets} integer
        onChange={sets => setConfig(current => ({ ...current, sets }))} onValidityChange={value => valid('sets', value)} />
    </div>
    <p className="my-6 text-sm text-muted-foreground">{t('fitness.builder.weightDuring')}</p>
    <button type="button" disabled={invalid.size > 0} onClick={() => { if (!invalid.size) onComplete(config); }}
      className="min-h-14 w-full rounded-xl bg-primary px-5 py-4 font-display text-lg font-semibold text-primary-foreground disabled:opacity-40">
      {t('fitness.workout.startBtn')}
    </button>
  </div>;
}
